import React, { useEffect } from 'react';

import { VBox } from '@/components/VBox';
import { HBox } from '@/components/HBox';

import i18n from '@/i18n';

import { Statistics } from '@/components/Statistics';

import { useSearchParam, useResource, useBaseRoute } from '@/hooks';
import moment from 'moment';
import { Box, Container, Button, Option, Select } from '@mui/joy';
import { ToggleButtonBox } from '@/components/ToggleButtonBox';
import { ToggleButton } from '@/components/ToggleButton';
import { CloudDownload } from '@mui/icons-material';
import { downloadStatistics } from '@/actions/aloader';
import { LoadingPage } from '@/components/LoadingPage';
import { ErrorView } from '@/components/ErrorView';
import { GenericList } from '@/components/GenericList';
import { Typography } from '@mui/material';
import { Img } from '@/components/Img';
import { Link } from 'react-router-dom';
import { ChartLineUp } from '@phosphor-icons/react';
import { VictoryPie } from 'victory';
import { useState } from 'react';
import { Case, SwitchCase } from '@/components/Syntax';
import { useMemo } from 'react';
import { useManager } from '@/hooks';

const METRICS = ['streams', 'saves', 'discoveries']

export function TopListContainer({
  start,
  end,
  dataKey,
  serviceIds
}) {
  const manager = useManager()

  const [topListViewType, setTopListViewType] = useState('list')

  const { data: top10Recordings, loading: loadingTop10Recordings } = useResource({
    queryKey: [manager?.type, manager?.slug, 'top', 10, 'recording'],
    path: `/${manager?.type}/${manager?.slug}/top/10/recordings`,
    enabled: !!manager,
    query: {
      start,
      end,
      serviceIds,
      ordering: 'top',
      limit: 20
    }
  })

  return (
    <VBox>
      <HBox>
        <Typography sx={{ flex: 1 }} level="h1" component="h1">{i18n.t('top-tracks')}</Typography>
        <ToggleButtonBox> 
          <ToggleButton
            active={topListViewType === 'list'}
            variant="outlined"
            onClick={() => {
              setTopListViewType("list")
            }}
          >
            {i18n.t('list')}
          </ToggleButton>
          <ToggleButton
            active={topListViewType === 'pie'}
            variant="outlined"
            onClick={() => {
              setTopListViewType("pie")
            }}
          >
            {i18n.t('pie-chart')}
          </ToggleButton>
        </ToggleButtonBox>
      </HBox>
      <SwitchCase value={topListViewType}>
        <Case match="list">
          <GenericList
            heading=""
            path={`/top/20/recordings`}
            queryKey={[manager?.type, manager?.slug, 'top', '20', 'recording', start, end]}
            key={`${start}${end}${serviceIds.join(',')}`}
            viewType="list"
            canSearch={false}
            canAdd={false}
            canPaginate={false}
            hasMoreMenu={false}
            showDefaultColumns={false}
            showToolBar={false}
            Icon={ChartLineUp}
            canSelectViewType={false}
            canChangeSortBy={false}
            columns={[
              {
                id: 'number',
                name: i18n.t('number'),
                type: 'number',
                render: ({ column, node, i }) => i 
              },
              {
                id: 'thumbnail_url',
                name: i18n.t('image'),
                type: 'image',
                render: ({ column, node }) => <Img src={node[column.id]} sx={{ width: '30pt', objectFit: 'contain' }} />
              },
              {
                id: 'name',
                name: i18n.t('nane'),
                type: 'text',
                render: ({ column, node }) => <Link to={`/${manager?.type}/${manager?.slug}/recording/${node.id}`}>{node[column.id]}</Link>
              },
              {
                id: 'totals',
                name: i18n.t('streams'),
                type: 'number',
                render: ({ column, node }) => {
                  const value = node[column.id]
                  if (isNaN(value)) {
                    return <Typography>-</Typography>
                  }
                  return (
                    <Typography sx={{ textAlign: 'right' }}>{value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                  )
                } 
              }
            ]}
            query={{
              start,
              end,
              serviceId: serviceIds?.length > 0 ? serviceIds[0] : null,
              ordering: 'top',
              limit: 20
            }}
          />
        </Case>
        <Case match="pie">
          {loadingTop10Recordings ? (
            <LoadingPage />
          ) : (top10Recordings?.results instanceof Array ? (
            <VictoryPie
              colorScale={'qualitative'}
              radius={100}
              style={{
                labels: {
                  fontSize: 5
                }
              }}
              data={
                top10Recordings?.results.map(
                  r => ({
                    x: r.name,
                    y: parseInt(r.totals)
                  })
                )
              }
            />
          ) : <ErrorView />)}
        </Case>
      </SwitchCase>
    </VBox>
  )
}

export function StatisticsContainer({
  start: defaultStart,
  end: defaultEnd,
  recordingIds=[],
  serviceIds=[],
  artistIds=[],
  releaseIds=[]
}) {
  const manager = useManager()
  const [end, setEnd] = useSearchParam('toDate', defaultEnd ?? moment().subtract(1, 'day').format('YYYY-MM-DD'))
  const [start, setStart] = useSearchParam('fromDate', defaultStart ?? moment().subtract(8, 'day').format('YYYY-MM-DD'))
  const [serviceId, setServiceId] = useSearchParam('serviceId', '')

  const [dataKey, setDataKey] = useSearchParam('dataKey', 'stream')

  const period = useMemo(() => {
    let ret = "custom"
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(1, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(8, 'day').format('YYYY-MM-DD')
    ) {
      ret = "7-days"
    }
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(1, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(29, 'day').format('YYYY-MM-DD')
    ) {
      ret = "28-days"
    }
    return ret
  }, [start, end])

  const setPeriod = (value) => {
    switch (value) {
      case "7-days":
        setEnd(moment().subtract(1, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(8, 'day').format('YYYY-MM-DD'))
        break;
      case "28-days":
        setEnd(moment().subtract(1, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(29, 'day').format('YYYY-MM-DD'))
        break;
    }
  }

  const baseRoute = useBaseRoute()

  const { data, isLoading } = useResource({
    queryKey: [...baseRoute.split(/\//g).slice(1), 'statistics', start, end],
    path: `${baseRoute}/statistics`,
    query: {
      start,
      end,
      recordingIds,
      serviceIds,
      artistIds,
      releaseIds
    }
  })

  const { data: servicesData, isLoadingServices } = useResource({
    queryKey: ['services'],
    path: '/services'
  })

  const services = servicesData?.results ?? []

  const handleDownloadStatsClick = event => {
    downloadStatistics({
      query: {
        start,
        end,
        [`${manager?.type}Id`]: manager?.slug,
        recordingIds,
        serviceIds,
        artistIds,
        releaseIds
      }
      
    })
  }

  return (
    <VBox>
      <Box sx={{ marginTop: 5, display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1 }}>
        <Typography>{start} - {end}</Typography>
        <Select
          sx={{ transitionDelay: `300ms`, minWidth: 200 }}
          placeholder={i18n.t('period')}
          defaultValue={period}
          onChange={(event, value) => {
            setPeriod(value)
          }}
        >
          <Option value="7-days">{i18n.t('7-days')}</Option>
          <Option value="28-days">{i18n.t('28-days')}</Option>
        </Select>
        <Select
          sx={{ transitionDelay: `300ms`, minWidth: 200 }}
          placeholder={i18n.t('dsp')}
          defaultValue={serviceId}
          onChange={(event, value) => {
            setServiceId(value)
          }}
        >
          <Option value="">{i18n.t('all-dsps')}</Option>
          {services?.map(service => <Option value={service.slug} key={service.slug}>{service.name}</Option>)}
        </Select>
        <ToggleButtonBox>
          {METRICS.map(metric => (
            <ToggleButton
              active={metric === dataKey}
              variant="outlined"
              onClick={() => {
                setDataKey(metric)
              }}
            >{i18n.t(metric)}</ToggleButton>
          ))}
        </ToggleButtonBox>
        <Button
          startDecorator={<CloudDownload />}
          variant="outlined"
          onClick={handleDownloadStatsClick}
        >
          {i18n.t('download-stats')}
        </Button>
      </Box>
      <StatisticsChartContainer
        period={period}
        start={start}
        end={end}
        dataKey={dataKey}
        serviceIds={[serviceId]}
      />
      <TopListContainer dataKey={dataKey} start={start} end={end} serviceIds={[serviceId]} />
    </VBox>
  )
}

export function StatisticsChartContainer({
  start,
  end,
  dataKey,
  recordingIds,
  serviceIds,
  artistIds,
  releaseIds
}) {
  const manager = useManager()
  const { data, isLoading } = useResource({
    queryKey: [manager?.type, manager?.slug, 'statistics', 'service', serviceIds?.join(',') ?? '', 'date', start, end, dataKey],
    path: `/${manager?.type}/${manager?.slug}/statistics`,
    query: {
      start,
      end,
      recordingIds,
      serviceIds,
      artistIds,
      releaseIds
    }
  })

  return (
    <VBox>
      <HBox>
        {isLoading ?
          <LoadingPage />
         : data ? (
          <Statistics
            key={dataKey}
            dataKey={dataKey}
            start={start}
            end={end}
            data={data }
          />
        ) : <ErrorView />}
      </HBox>
    </VBox>
  )
}

export default function StatisticsPage() {
  return (
    <Container>
      <VBox>
        <h1>{i18n.t('statistics')}</h1>
        <StatisticsContainer />
      </VBox>
    </Container>
  )
}